import Moment from 'react-moment';
import { PermissionCombinationType, useAccess } from '@sitemax/smx-permission';
import { TableProps, Typography, Button, Space, Tooltip } from 'antd';
import { CheckCircleOutlined, DeleteFilled, SelectOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';

import { setDocument } from 'slices/documentSlice';
import { copyDocumentVersion } from 'slices/documentsActions';
import { deleteDocumentVersion, fetchDocumentVersions, downloadDocumentVersion, fetchDocumentVersion, fetchDocuments } from 'slices/documentsActions';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { useModal } from 'hooks/useModal';
import { Document } from 'interfaces/documents';
import { addSnackbar } from 'slices/snackbarsSlice';

const TableColumns = ({ document, closeModal }: { document: Document; closeModal: () => void; }) => {
    const { openModal } = useModal();
    const dispatch = useAppDispatch();
    const { breadcrumbs } = useAppSelector((state) => state.documents);
    const isProjectDocument = breadcrumbs?.length > 0 ? breadcrumbs[breadcrumbs.length - 1].is_project_document : false;
    const canView = useAccess(isProjectDocument ? ['files.project.view'] : ['files.all.view']);
    const canEdit = useAccess(isProjectDocument ? ['files.project.edit'] : ['files.all.edit']);
    const canManage = useAccess(isProjectDocument ? ['files.project.manage'] : ['files.all.manage']);

    const handleConfirmRowSetLatest = (item: any) => {
        dispatch(
            copyDocumentVersion({
                id: document?.id,
                version: item?.id,
            }),
        )
            .unwrap()
            .then(async (response) => {
                await dispatch(fetchDocumentVersions(document?.id));
                dispatch(
                    addSnackbar({
                        message: 'File version copied successfully',
                        type: 'success',
                    }),
                );
                dispatch(fetchDocuments(document?.category_id));
                closeModal();
            })
            .catch();
    };

    const handleRowSetLatest = (item: any) => {
        openModal(
            'confirmationModal',
            {
                text: 'Are you sure want to make a copy of this version as a new file?',
            },
            {
                onConfirm: () => {
                    handleConfirmRowSetLatest(item);
                },
            },
        );
    };

    const handleConfirmRowDelete = (record: any) => {
        if (!document) return;
        if (!record) return;

        dispatch(
            deleteDocumentVersion({
                id: document?.id,
                version: record?.id,
            }),
        )
            .unwrap()
            .then(() => dispatch(fetchDocumentVersions(document?.id)));
    };

    const handleRowDelete = (record: any) => {
        openModal(
            'confirmationModal',
            {
                text: 'Are you sure want to delete version?',
            },
            {
                onConfirm: () => handleConfirmRowDelete(record),
            },
        );
    };

    const handleViewVersion = async (record: any) => {
        const versionResult = await dispatch(
            fetchDocumentVersion({
                id: document?.id,
                version: record?.id,
            })
        );

        if (versionResult.payload?.data) {
            if (document?.mime_type === 'application/pdf') {
                const versionData = versionResult.payload.data;
                const documentWithVersionUrl: Document = {
                    ...document,
                    url: versionData.url,
                };

                dispatch(setDocument(documentWithVersionUrl));
                openModal('documentMarkupModal', {
                    readonly: true,
                });
            } else {
                window.open(versionResult.payload.data.url, '_blank');
            }
        }
    };

    const handleDownloadVersion = (record: any) => {
        dispatch(
            downloadDocumentVersion({
                id: document?.id,
                version: record?.id,
                name: document?.name,
            }),
        );
    };

    const columns: TableProps<any>['columns'] = [
        {
            title: 'Version No.',
            dataIndex: 'version_number',
            key: 'version_number',
            render: (_, record) => <Typography>{record?.version_number}</Typography>,
        },
        {
            title: 'Uploaded/Created by',
            dataIndex: 'metadata.user.name',
            key: 'metadata.user.name',
            render: (_, record) => <Typography>{record?.metadata?.user?.name}</Typography>,
        },
        {
            title: 'Date',
            dataIndex: 'last_touched_at',
            key: 'last_touched_at',
            render: (_, record) => <Moment date={record.last_touched_at || record.updated_at} format="lll" />,
        },
        {
            title: 'Size',
            dataIndex: 'size_formatted',
            key: 'size_formatted',
        },
        {
            title: 'Is Latest Upload',
            dataIndex: 'is_base',
            key: 'is_base',
            align: 'center',
            render: (_, record) => record.is_base ? <CheckCircleOutlined style={{ fontSize: 20 }} /> : <></>,
        },
    ];

    if (canView || canEdit || canManage) {
        columns.push({
            key: 'actions',
            title: 'Actions',
            dataIndex: 'actions',
            render: (_, record) => {
                return (
                    <Space size="small">
                        {
                            canView &&
                            <>
                                <Tooltip title="View/Download">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={() => handleViewVersion(record)}
                                    />
                                </Tooltip>
                                <Tooltip title="Download">
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        onClick={() => handleDownloadVersion(record)}
                                    />
                                </Tooltip>
                            </>
                        }
                        {
                            canEdit &&
                            <Tooltip title="Make copy as new file">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<SelectOutlined />}
                                    onClick={() => handleRowSetLatest(record)}
                                />
                            </Tooltip>
                        }
                        {
                            canManage &&
                            !record.is_base && !record.is_latest &&
                            <Tooltip title="Delete Version">
                                <Button
                                    danger
                                    type="text"
                                    size="small"
                                    icon={<DeleteFilled />}
                                    onClick={() => handleRowDelete(record)}
                                />
                            </Tooltip>
                        }
                    </Space>
                );
            },
        });
    }

    return columns;
};

export default TableColumns;
